import { useChatStatus } from '@ai-sdk-tools/store';
import type { ChatStatus } from 'ai';
import { useEffect, useRef } from 'react';

import { normalizeChatUuid } from '../lib/notification-helpers';

export interface UseAutoResumeParams {
  /**
   * Whether auto-resume is enabled.
   * When true, the hook will automatically resume interrupted streams.
   */
  autoResume: boolean;
  /**
   * The active chat id. Existing chats probe the backend stream endpoint on
   * activation instead of inferring resumability from stale local state.
   */
  chatUuid: string;
  /**
   * Brand-new optimistic chats do not have a server stream to reconnect to yet.
   */
  isNewChat: boolean;
  /**
   * Backend stream id for the active stream, if known. When the server reports
   * a new stream id for the same chat, the probe runs again for that stream.
   */
  activeStreamId?: string | null;
  /**
   * Function to resume the stream.
   * Should be called when auto-resume is enabled for an existing chat activation.
   */
  resumeStream: () => void;
}

export function shouldAutoResume({
  autoResume,
  isNewChat,
  status,
}: {
  autoResume: boolean;
  isNewChat: boolean;
  status: ChatStatus;
}): boolean {
  if (!autoResume || isNewChat) {
    return false;
  }

  if (status !== 'ready') {
    return false;
  }

  return true;
}

export function getAutoResumeAttemptKey({
  activeStreamId,
  chatUuid,
  isNewChat,
}: {
  activeStreamId?: string | null;
  chatUuid: string;
  isNewChat: boolean;
}): string | null {
  if (isNewChat) {
    return null;
  }

  if (activeStreamId) {
    return `stream:${activeStreamId}`;
  }

  return `chat:${normalizeChatUuid(chatUuid)}`;
}

/**
 * Hook that automatically resumes interrupted chat streams.
 * Based on the Vercel AI Chatbot implementation: https://github.com/vercel/ai-chatbot/blob/d0b6f37aeee532b942245ee9d406312f79e2fabe/hooks/use-auto-resume.ts
 *
 * When autoResume is enabled for an existing chat, this hook probes the
 * backend stream endpoint once per activation and lets the server decide
 * whether there is a live stream to resume — a 204 response simply means
 * there is nothing to reconnect to. The probe is deduped per activation key
 * (chat id, or stream id when known); no client-side resumability state is
 * kept beyond the lifetime of this hook.
 *
 * @example
 * ```tsx
 * function ChatComponent() {
 *   const chat = useChat({ resume: true, ... });
 *   const { resumeStream } = chat; // or from useChatActions
 *
 *   useAutoResume({
 *     autoResume: true,
 *     chatUuid,
 *     isNewChat,
 *     resumeStream,
 *   });
 *
 *   return <div>...</div>;
 * }
 * ```
 */
export function useAutoResume({ activeStreamId, autoResume, chatUuid, isNewChat, resumeStream }: UseAutoResumeParams) {
  const status = useChatStatus();
  const attemptedKeysRef = useRef<{ chatKey: string; keys: Set<string> }>({ chatKey: '', keys: new Set() });

  // Attempted keys are scoped to the active chat so revisiting a chat probes
  // again, while key changes within one activation are deduped as a set: the
  // key can legitimately flip back to an earlier value (stream id arrives,
  // then clears after the turn), and a flip back must not probe again.
  const chatKey = normalizeChatUuid(chatUuid);
  if (attemptedKeysRef.current.chatKey !== chatKey) {
    attemptedKeysRef.current = { chatKey, keys: new Set() };
  }

  const attemptKey = getAutoResumeAttemptKey({
    activeStreamId,
    chatUuid,
    isNewChat,
  });

  // While this client's own request is in flight it is already attached to the
  // stream, so any key observed now (the chat key, or the stream id once the
  // polled chats list reports it) is nothing to resume later. Recording them
  // prevents the post-turn key flips from re-probing our own finished turn.
  //
  // The status is store-global: on the first render after switching chats it
  // can still reflect the previous chat's in-flight stream, so only renders of
  // the same chat as the previous one count as "our own request".
  const prevRenderChatKeyRef = useRef('');
  useEffect(() => {
    const isSameChatAsPreviousRender = prevRenderChatKeyRef.current === chatKey;
    prevRenderChatKeyRef.current = chatKey;

    if (isSameChatAsPreviousRender && (status === 'submitted' || status === 'streaming') && attemptKey) {
      attemptedKeysRef.current.keys.add(attemptKey);
    }
  }, [attemptKey, chatKey, status]);

  useEffect(() => {
    if (
      !shouldAutoResume({
        autoResume,
        isNewChat,
        status,
      })
    ) {
      return;
    }

    if (!attemptKey || attemptedKeysRef.current.keys.has(attemptKey)) {
      return;
    }

    attemptedKeysRef.current.keys.add(attemptKey);
    resumeStream();
  }, [attemptKey, autoResume, isNewChat, resumeStream, status]);
}
